import { getApiClient, getStudioClient } from '../../../stores'
import _ from 'lodash'
import { WebchatIntegrationInput, initialConfig, LATEST_WEBCHAT_CONFIG_VERSION } from '@bpinternal/shared'
import { useAppStore } from '~/stores'

const LLM_INTEGRATIONS = ['openai', 'cerebras', 'groq', 'anthropic', 'fireworks-ai', 'google-ai']
const UTILITY_INTEGRATIONS = ['charts', 'pdf-generator', 'browser']
const DATA_CONNECTOR_INTEGRATIONS = ['notion-kb']
const PUBLIC_LABS_INTEGRATIONS = ['agi/improvement']
const CHAT_INTEGRATIONS = ['webchat']

const DEFAULT_INTEGRATIONS = [
  ...LLM_INTEGRATIONS,
  ...DATA_CONNECTOR_INTEGRATIONS,
  ...UTILITY_INTEGRATIONS,
  ...PUBLIC_LABS_INTEGRATIONS,
  ...CHAT_INTEGRATIONS,
]

const DEFAULT_NEW_BOT_NAME = 'New Agent'

type BotConfiguration = {
  workspaceId: string
  botId: string
  name: string
  webchatConfig: Partial<WebchatIntegrationInput>
}

const activeReconfigureRequests = new Map<string, Promise<void>>()

export async function reconfigureBot({ workspaceId, botId, ...config }: BotConfiguration): Promise<void> {
  const apiClient = getApiClient({ workspaceId })

  // Skip if already reconfiguring
  const requestKey = `${workspaceId}-${botId}-reconfigure`
  if (activeReconfigureRequests.has(requestKey)) {
    await activeReconfigureRequests.get(requestKey)
    return
  }

  const reconfigurePromise = (async () => {
    const { integration: webchatIntegration } = await apiClient.getPublicIntegration({
      name: 'webchat',
      version: '0.3.0',
    })

    const { bot } = await apiClient.getBot({ id: botId })
    const currentWebchatConfig =
      (bot.integrations?.[webchatIntegration.id]?.configuration as WebchatIntegrationInput) || {}

    const updatedConfig: WebchatIntegrationInput = _.merge({}, currentWebchatConfig, config.webchatConfig)

    const webChatIntegration: Record<string, { enabled: boolean; configuration: any }> = {
      [webchatIntegration.id]: { enabled: true, configuration: updatedConfig },
    }

    await apiClient.updateBot({
      id: botId,
      name: config.name || DEFAULT_NEW_BOT_NAME,
      integrations: webChatIntegration,
    })
  })()

  activeReconfigureRequests.set(requestKey, reconfigurePromise)

  try {
    await reconfigurePromise
  } catch (error) {
    console.error('Failed to reconfigure bot:', error)
  } finally {
    activeReconfigureRequests.delete(requestKey)
  }
}

export const installIntegrationsAndPlugins = async (workspaceId: string, botId: string) => {
  const apiClient = getApiClient({ workspaceId })
  const defaultIntegrations = await getLatestDefaultIntegrations(workspaceId)
  const insightsPlugin = await getLatestInsightsPlugin(workspaceId)
  const needPlanUpgrade = useAppStore.getState().activeWorkspace?.plan === 'community'

  const integrationPayload = defaultIntegrations.reduce(
    (acc, integration) => {
      if (integration.name === 'webchat') {
        acc[integration.id] = {
          enabled: true,
          configuration: {
            ...initialConfig,
            primaryColor: initialConfig.color,
            borderRadiusScale: initialConfig.radius,
            version: LATEST_WEBCHAT_CONFIG_VERSION,
          },
        }
      } else {
        acc[integration.id] = {
          enabled: true,
          configuration: {},
        }
      }
      return acc
    },
    {} as Record<string, { enabled: boolean; configuration: object }>
  )

  try {
    await apiClient.updateBot({
      id: botId,
      integrations: integrationPayload,
      plugins: {
        ['conversation-insights']: {
          id: insightsPlugin.id,
          enabled: true,
          configuration: { aiEnabled: needPlanUpgrade ? false : true },
        },
      },
    })
  } catch (error) {
    console.error('Failed to update bot:', error)
  }
}

const getLatestDefaultIntegrations = async (workspaceId: string) => {
  const apiClient = getApiClient({ workspaceId })

  const integrations = await Promise.all(
    DEFAULT_INTEGRATIONS.map((name) =>
      apiClient
        .getPublicIntegration({
          name,
          version: name === 'webchat' ? '0.3.0' : 'latest',
        })
        .then((i) => i.integration)
        .catch(() => null)
    )
  )
  return integrations.filter((i) => i !== null) as NonNullable<(typeof integrations)[0]>[]
}

const getLatestInsightsPlugin = async (workspaceId: string) => {
  const apiClient = getApiClient({ workspaceId })
  const { plugin } = await apiClient.getPublicPlugin({ name: 'conversation-insights', version: 'latest' })
  return plugin
}

const activePublishRequests = new Map<string, Promise<void>>()

export const publishBotViaBotJson = async (workspaceId: string, botId: string, botJson: Record<string, unknown>) => {
  const requestKey = `${workspaceId}-${botId}-publish`

  // Skip if there's already a publish request in progress
  if (activePublishRequests.has(requestKey)) {
    await activePublishRequests.get(requestKey)
    return
  }

  const publishPromise = (async () => {
    const studioClient = getStudioClient({ botId: botId, workspaceId })

    for (let attempt = 1; attempt <= 3; attempt++) {
      try {
        await studioClient.post('/publish-from-bot-json', botJson)
        return
      } catch (error) {
        if (attempt === 3) {
          console.error('Failed to publish bot:', error)
        }

        await new Promise((resolve) => setTimeout(resolve, attempt * 1000))
      }
    }
  })()

  activePublishRequests.set(requestKey, publishPromise)

  try {
    await publishPromise
  } finally {
    activePublishRequests.delete(requestKey)
  }
}

export const findAutonomousNode = (botJson: any): any => {
  if (!botJson.flows) return null

  for (const flow of botJson.flows) {
    if (!flow.nodes) continue

    for (const node of flow.nodes) {
      if (node.type === 'autonomous') {
        return node
      }
    }
  }

  return null
}
